<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { fetchWithHeaders } from "@/utils";
const props = defineProps({
    matricule: {
        type: String,
        default: "none",
    },
});

const img = ref(null);
const container = ref(null);
const loadError = ref(null);
const isLoading = ref(true);
const isVisible = ref(false);
let observer = null;

async function loadPicture() {
    if (!isVisible.value) return;

    const url = `${window._env_.API_ENDPOINT}/students/${props.matricule}/picture/`;
    loadError.value = null;
    isLoading.value = true;

    try {
        // Gestionnaire d'erreur silencieux pour éviter les popups
        const res = await fetchWithHeaders(url, {}, () => {
            // Gestion silencieuse - pas de popup pour les erreurs de photos
        });

        // Vérifier si la réponse existe et est OK avant de traiter l'image
        if (!res || !res.ok) {
            const statusCode = res?.status || 'inconnu';
            loadError.value = statusCode;

            // Log pour les admins avec détails
            if (statusCode === 404) {
                console.warn(`📷 Photo non disponible pour l'étudiant ${props.matricule} (404 - Non trouvée sur le serveur)`);
            } else if (statusCode === 403) {
                console.warn(`📷 Photo inaccessible pour l'étudiant ${props.matricule} (403 - Accès refusé, vérifier les permissions SMB)`);
            } else if (statusCode === 500) {
                console.error(`📷 Erreur serveur lors du chargement de la photo pour ${props.matricule} (500 - Erreur backend, vérifier les logs)`);
            } else {
                console.warn(`📷 Photo non chargée pour l'étudiant ${props.matricule} (Status: ${statusCode})`);
            }

            isLoading.value = false;
            return;
        }

        // Vérifier si un fallback a été utilisé (header personnalisé)
        const isFallback = res.headers.get('X-Photo-Fallback');
        const fallbackReason = res.headers.get('X-Photo-Fallback-Reason');

        if (isFallback === 'true') {
            console.warn(`📷 Photo de fallback utilisée pour l'étudiant ${props.matricule} - Raison: ${fallbackReason}`);
            loadError.value = 'fallback';
        }

        const imgdata = await res.blob();
        if (img.value) {
            img.value.src = URL.createObjectURL(imgdata);
            if (!isFallback) {
                loadError.value = null;
            }
        }
        isLoading.value = false;
    } catch (error) {
        loadError.value = 'error';
        console.error(`📷 Erreur lors du chargement de la photo pour ${props.matricule}:`, error.message);
        isLoading.value = false;
    }
}

onMounted(() => {
    // Lazy loading avec IntersectionObserver
    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !isVisible.value) {
                    isVisible.value = true;
                    loadPicture();
                    // Une fois chargée, arrêter d'observer
                    if (observer && container.value) {
                        observer.unobserve(container.value);
                    }
                }
            });
        },
        {
            rootMargin: "100px", // Commencer à charger 100px avant d'être visible
        }
    );

    if (container.value) {
        observer.observe(container.value);
    }
});

onUnmounted(() => {
    if (observer && container.value) {
        observer.unobserve(container.value);
    }
});
</script>
<template>
    <div ref="container" class="matricule-img-container">
        <img ref="img" alt="Photo étudiant" />
        <q-badge
            v-if="loadError && loadError !== 'fallback'"
            floating
            color="warning"
            :label="loadError === 404 ? '404' : loadError === 403 ? '403' : '!'"
            :title="
                loadError === 404
                    ? 'Photo non trouvée sur le serveur'
                    : loadError === 403
                    ? 'Accès refusé - Vérifier les permissions SMB'
                    : loadError === 500
                    ? 'Erreur serveur - Consulter les logs backend'
                    : 'Erreur de chargement'
            "
        />
        <q-badge
            v-if="loadError === 'fallback'"
            floating
            color="info"
            label="FB"
            title="Photo de fallback utilisée - Voir console pour détails (📷)"
        />
    </div>
</template>
<style scoped>
.matricule-img-container {
    position: relative;
    display: inline-block;
    width: 80px;
    height: 100px;
}

img {
    /* Contraindre la taille de l'image */
    width: 80px;
    height: 100px;
    background-color: #e0e0e0;
    object-fit: cover;
    display: block;
}
</style>
